import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";

const EMPTY = { title: "", date: new Date().toISOString().slice(0, 10), image_url: "", content: "", published: true };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

export default function NewsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/news").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/news/${editingId}`, form);
        toast.success("Actualité mise à jour");
      } else {
        await api.post("/admin/news", form);
        toast.success("Actualité publiée");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, date: item.date, image_url: item.image_url || "", content: item.content, published: item.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette actualité ?")) return;
    await api.delete(`/admin/news/${id}`).catch(() => {});
    toast.success("Actualité supprimée");
    load();
  };

  return (
    <div data-testid="news-manager" className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
          {editingId ? "Modifier l'actualité" : "Publier une actualité"}
        </h2>
        <div>
          <label className={labelClass}>Titre *</label>
          <input data-testid="news-title-input" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>Date *</label>
          <input data-testid="news-date-input" type="date" className={inputClass} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>Image (facultatif)</label>
          <input data-testid="news-image-input" className={inputClass} placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, image_url: u })))} />
          <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
          <div className="mt-2">
            <FileUploadButton testId="news-image-upload" accept="image/*" label="Ou téléverser une image" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Texte *</label>
          <textarea data-testid="news-content-input" rows="6" className={inputClass} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input data-testid="news-published-checkbox" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#B83227]" />
          Visible sur le site
        </label>
        <div className="flex gap-3">
          <button type="submit" data-testid="news-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
            {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Publier"}
          </button>
          {editingId && (
            <button type="button" data-testid="news-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Actualités en ligne ({items.length})</h2>
        {items.length === 0 && <p className="text-sm text-stone-500">Aucune actualité.</p>}
        {items.map((n) => (
          <div key={n.id} data-testid={`news-item-${n.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1E1B18]">{n.title}</p>
                <p className="text-xs text-stone-500">{n.date} · {n.published ? "Visible" : "Masquée"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(n)} data-testid={`news-edit-${n.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(n.id)} data-testid={`news-delete-${n.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
